import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button, Space, message, Typography, Checkbox } from "@mimber/ui-web"
import {
  Form,
  InputField,
  ZipCodeField,
  StateField,
  CountryField,
} from "@mimber/forms"
import { useUpdateOrganizationProfile } from "@mimber/client/mimber-organization"
import type { OrganizationBillingDetailsFormProps } from "./OrganizationBillingDetailsForm.types"
import {
  organizationBillingDetailsSchema,
  type OrganizationBillingDetailsFormData,
} from "./OrganizationBillingDetailsForm.schema"

const { Paragraph } = Typography

export const OrganizationBillingDetailsForm: React.FunctionComponent<
  OrganizationBillingDetailsFormProps
> = ({ organization, onSuccess, disabled = false }) => {
  const [sameAsOrganizationAddress, setSameAsOrganizationAddress] =
    useState(false)
  const updateProfile = useUpdateOrganizationProfile(organization.uuid)

  const form = useForm<OrganizationBillingDetailsFormData>({
    defaultValues: {
      billingAddress: organization.billingAddress || "",
      billingCity: organization.billingCity || "",
      billingState: organization.billingState || "",
      billingZipCode: organization.billingZipCode || "",
      billingCountry: organization.billingCountry || "",
      billingContactName: organization.billingContactName || "",
      billingContactEmail: organization.billingContactEmail || "",
    },
    resolver: zodResolver(organizationBillingDetailsSchema),
    mode: "onBlur",
  })

  const {
    formState: { isDirty },
    reset,
    setValue,
  } = form

  const handleSameAsOrganizationAddressChange = (checked: boolean) => {
    setSameAsOrganizationAddress(checked)

    if (checked) {
      setValue("billingAddress", organization.address || "", {
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue("billingCity", organization.city || "", {
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue("billingState", organization.state || "", {
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue("billingZipCode", organization.zipCode || "", {
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue("billingCountry", organization.country || "", {
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }

  const onSubmit = (data: OrganizationBillingDetailsFormData) => {
    const payload = {
      billingAddress: data.billingAddress || null,
      billingCity: data.billingCity || null,
      billingState: data.billingState || null,
      billingZipCode: data.billingZipCode || null,
      billingCountry: data.billingCountry || null,
      billingContactName: data.billingContactName || null,
      billingContactEmail: data.billingContactEmail || null,
    }

    updateProfile.mutate(payload, {
      onSuccess: () => {
        message.success("Billing details updated successfully")
        reset(data)
        onSuccess?.()
      },
      onError: (error) => {
        message.error(error.message || "Failed to update billing details")
      },
    })
  }

  const isLoading = updateProfile.isPending

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={disabled || isLoading}
      layout="vertical"
    >
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Manage the billing address and contact used for invoices and receipts
      </Paragraph>

      <Checkbox
        checked={sameAsOrganizationAddress}
        disabled={disabled || isLoading}
        onChange={(e) =>
          handleSameAsOrganizationAddressChange(e.target.checked)
        }
        style={{ marginBottom: 24 }}
      >
        Same as organization address
      </Checkbox>

      <InputField
        name="billingAddress"
        label="Billing Address"
        placeholder="Street address"
      />

      <InputField name="billingCity" label="Billing City" placeholder="City" />

      <StateField name="billingState" label="Billing State" />

      <ZipCodeField
        name="billingZipCode"
        label="Billing Zip Code"
        placeholder="Zip code"
      />

      <CountryField name="billingCountry" label="Billing Country" />

      <InputField
        name="billingContactName"
        label="Billing Contact Name"
        placeholder="Billing contact name"
      />

      <InputField
        name="billingContactEmail"
        label="Billing Contact Email"
        placeholder="Billing contact email"
      />

      <Space style={{ marginTop: 24 }}>
        <Button
          type="primary"
          htmlType="submit"
          disabled={disabled || !isDirty}
          loading={isLoading}
        >
          Save Changes
        </Button>

        <Button
          onClick={() => reset()}
          disabled={disabled || !isDirty || isLoading}
        >
          Reset
        </Button>
      </Space>
    </Form>
  )
}
